import config from 'garaje/config/environment';
import Mirage, { Collection } from 'miragejs';
import { getUnixTime } from 'date-fns';
import { createResourceState } from 'garaje/mirage/helpers/resource-states';
import { AvailabilityStatus, ResourceType } from 'garaje/services/live-map';

export default function () {
  this.urlPrefix = config.mapsHost;

  // **************************************************************************
  // Area Maps & Map Floors
  // **************************************************************************

  this.post('/a/maps/api/v2/map-features/bulk-create', function (schema, request) {
    const featuresToCreate = JSON.parse(request.requestBody)['atomic:operations'];
    const featuresToReturn = featuresToCreate.map((feature) => {
      // (ar) set up relationships
      const mapFloorId = feature.data.relationships['map-floor'].data.id;
      const areaMapId = feature.data.relationships['area-map'].data.id;
      const mapFloor = schema.mapFloors.find(mapFloorId);
      const areaMap = schema.areaMaps.find(areaMapId);

      return schema.mapFeatures.create({
        name: feature.data.attributes.name,
        enabled: feature.data.attributes.enabled,
        // (ar) the server returns lowercased types
        type: feature.data.attributes.type.toLowerCase().replace(/_/g, '-'),
        'external-id': feature.data.attributes.externalId,
        'parent-feature-id': feature.data.attributes.parentFeatureId,
        notes: feature.data.attributes.notes,
        geometry: {
          type: feature.data.attributes.geometry.type,
          coordinates: feature.data.attributes.geometry.coordinates,
        },
        mapFloor: mapFloor,
        areaMap: areaMap,
      });
    });
    return {
      data: featuresToReturn.map((feature) => {
        return {
          id: feature.attrs.id,
          attributes: {
            name: feature.attrs.name,
            notes: feature.attrs.notes,
            enabled: feature.attrs.enabled,
            geometry: {
              type: feature.attrs.geometry.type,
              coordinates: feature.attrs.geometry.coordinates,
            },
            type: feature.attrs.type,
            'external-id': feature.attrs['external-id'],
          },
          relationships: {
            'map-floor': {
              data: {
                id: feature.mapFloorId,
              },
            },
          },
        };
      }),
    };
  });
  this.post('/a/maps/api/v1/map-features/bulk-update', function (schema, request) {
    const featuresToUpdate = JSON.parse(request.requestBody)['atomic:operations'];
    featuresToUpdate.forEach((feature) => {
      schema.db.mapFeatures.update(feature.data.attributes.id, { ...feature.data.attributes });
    });
    return schema.mapFeatures.find(featuresToUpdate.map((it) => it.data.attributes.id)).models;
  });

  this.post('/a/maps/api/v2/map-features/bulk-delete', function (schema, request) {
    const featuresToDelete = JSON.parse(request.requestBody)['atomic:operations'];
    featuresToDelete.map((feature) => {
      schema.db.mapFeatures.remove(feature.data.id);
    });
    return new Mirage.Response(204, {}, {});
  });

  this.post('/a/maps/api/v1/area-maps');
  this.get('/a/maps/api/v1/area-maps/:id');
  this.get('/a/maps/api/v1/area-maps', function (schema, { queryParams }) {
    const locationId = queryParams['filter[location]'];
    const areaMaps = schema.areaMaps.where(({ entityId }) => entityId === locationId);

    return areaMaps;
  });

  this.post('/a/maps/api/v1/drafts', function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const draft = schema.drafts.create({
      name: 'New draft',
      createdBy: body.data.attributes['created-by'],
      lastUpdatedBy: body.data.attributes['created-by'],
      createdAt: getUnixTime(new Date()),
      lastUpdatedAt: getUnixTime(new Date()),
    });
    const originalAreaMap = schema.areaMaps.first();
    const draftAreaMap = schema.areaMaps.create({
      drafts: [draft],
      entityId: originalAreaMap.entityId,
      entityType: originalAreaMap.entityType,
    });
    const draftMapFloors = [];
    for (let i = 0; i < originalAreaMap.mapFloors.models.length; i++) {
      const ogMapFloor = originalAreaMap.mapFloors.models[i];
      const attrs = { ...ogMapFloor.attrs };
      delete attrs.id;
      const newFloor = schema.mapFloors.create({ ...attrs });
      schema.floors.create({ id: newFloor.id });
      draftMapFloors.push(newFloor);
    }

    draftAreaMap.update({ mapFloors: draftMapFloors });
    draft.update({ draftAreaMap, originalAreaMap });

    return draft;
  });
  this.patch('/a/maps/api/v1/drafts/:id');
  this.delete('/a/maps/api/v1/drafts/:id');

  this.patch('/a/maps/api/v1/drafts/:id/relationships/employees', (schema, request) => {
    const { id } = request.params;
    const draft = schema.drafts.find(id);
    const { data } = JSON.parse(request.requestBody);

    if (!draft) return new Mirage.Response(404);

    const employeeIds = data.map((i) => i.id);

    draft.update({
      employeeIds: employeeIds,
    });

    return new Mirage.Response(204);
  });

  this.post('/a/maps/api/v1/drafts/:id/publish');
  this.put('/a/maps/api/v1/drafts/:id/publish', function () {
    return new Mirage.Response(204);
  });

  this.get('/a/maps/api/v1/map-floors', function (schema, { queryParams }) {
    let mapFloors = schema.mapFloors.all();

    const queryFilter = queryParams['filter[query]'];
    if (queryFilter) {
      mapFloors = mapFloors.filter(({ name }) => name.includes(queryFilter));
    }
    return mapFloors;
  });

  this.get('/a/maps/api/v1/map-floors/:id');
  this.patch('/a/maps/api/v1/map-floors/:id');
  this.delete('/a/maps/api/v1/map-floors/:id');
  this.post('/a/maps/api/v1/map-floors', function (schema, _request) {
    const attrs = this.normalizedRequestAttrs();

    if (!attrs.name || !attrs.areaMapId) {
      return new Mirage.Response(
        422,
        {},
        {
          errors: [
            {
              status: '422',
              title: 'Unprocessable Entity',
              detail: 'Name and area map are required',
            },
          ],
        },
      );
    } else {
      return schema.mapFloors.create(attrs);
    }
  });

  this.patch('/a/maps/api/v1/map-floors/:id/raster-image', function (schema, request) {
    const file = request.requestBody.get('file');
    schema.db.mapFloors.update(request.params.id, {
      rasterImageName: file?.name,
      rasterImageUrl: `https://picsum.photos/seed/${file?.name}/1200/800`,
    });

    return new Mirage.Response(204);
  });

  this.get('/a/maps/api/v1/map-features/:id');
  this.delete('/a/maps/api/v1/map-features/:id');
  this.patch('/a/maps/api/v1/map-features/:id');
  this.post('/a/maps/api/v1/map-features', function (schema, _request) {
    const attrs = this.normalizedRequestAttrs();

    if (!attrs.name) {
      return new Mirage.Response(
        422,
        {},
        {
          errors: [
            {
              status: '422',
              title: 'Unprocessable Entity',
              detail: 'Name is required',
            },
          ],
        },
      );
    } else {
      return schema.mapFeatures.create(attrs);
    }
  });

  this.get('/a/maps/api/v1/map-features', function ({ mapFeatures }, { queryParams }) {
    const floorId = queryParams['filter[floor]'];
    const areaMapId = queryParams['filter[area-map]'];
    const typeFilter = queryParams['filter[type]'];

    if (areaMapId) {
      return mapFeatures.where(({ areaMapId, type }) => {
        return areaMapId === areaMapId && (typeFilter ? typeFilter.split(',').includes(type) : true);
      });
    }
    if (floorId) {
      return mapFeatures.where(({ mapFloorId, type }) => {
        return mapFloorId === floorId && (typeFilter ? typeFilter.split(',').includes(type) : true);
      });
    }

    return mapFeatures.all();
  });

  this.post('/a/maps/api/v1/map-versions');
  this.get('/a/maps/api/v1/map-versions');
  this.get('/a/maps/api/v1/map-versions/:id', function (schema, request) {
    let mapVersion = null;

    if (request.params.id === 'latest') {
      const totalMapVersions = schema.mapVersions.all().length;
      mapVersion = schema.mapVersions.find(totalMapVersions);
    }

    if (request.params.id !== 'latest') {
      mapVersion = schema.mapVersions.find(request.params.id);
    }

    const areaMap = mapVersion.areaMap;
    const mapFloors = areaMap.mapFloors;
    const mapFeatures = mapFloors.models.reduce(
      (mapFeatures, mapFloor) => [...mapFeatures, ...mapFloor.mapFeatures.models],
      [],
    );

    const mapFloorCollection = new Collection('map-floor', mapFloors.models);
    const mapFeatureCollection = new Collection('map-feature', mapFeatures);

    const { data: includedMapFloors } = this.serializerOrRegistry.serialize(mapFloorCollection, request);
    const { data: includedMapFeatures } = this.serializerOrRegistry.serialize(mapFeatureCollection, request);

    const included = [...includedMapFloors, ...includedMapFeatures];

    const response = {
      data: {
        id: mapVersion.id,
        type: 'map-versions',
        attributes: {
          'created-at': mapVersion.createdAt,
        },
        relationships: {
          'area-map': {
            data: {
              id: areaMap.id,
              type: 'area-maps',
            },
          },
          employees: {
            data: mapVersion.employeeIds.map((employeeId) => ({
              id: employeeId,
              type: 'employees',
            })),
          },
        },
      },
      included,
    };

    return new Mirage.Response(200, {}, response);
  });

  this.get('/a/maps/api/v1/live-map', (schema, request) => {
    const reservations = schema.reservations.all().models;
    const floor = schema.floors.first();
    const floorDesks = schema.desks.where({ floorId: floor.id }).models;
    const rooms = schema.rooms.all().models;

    const employees = schema.employees.all().models;

    const resourceStates = [];
    floorDesks.forEach((desk) => {
      let reservationId = null;
      if (desk.reservationIds.length !== 0) {
        reservationId = desk.reservationIds[0];
      }
      const reservation = reservations.find((reservation) => reservation.id === reservationId);
      const currentEmployee = employees.find((employee) => employee.email === reservation?.userEmail);

      if (currentEmployee && reservation) {
        resourceStates.push(
          createResourceState(
            desk,
            ResourceType.DESK,
            AvailabilityStatus.OCCUPIED,
            reservation?.id || null,
            currentEmployee.id,
          ),
        );
      } else {
        resourceStates.push(createResourceState(desk, ResourceType.DESK, AvailabilityStatus.AVAILABLE, null, null));
      }
    });

    rooms.forEach((room) => {
      if (room.timeAvailableInMinutes >= 0) {
        resourceStates.push(createResourceState(room, ResourceType.ROOM, AvailabilityStatus.AVAILABLE, null, null));
      } else {
        resourceStates.push(createResourceState(room, ResourceType.ROOM, AvailabilityStatus.OCCUPIED, null, null));
      }
    });

    const mapVersion = schema.mapVersions.first();
    const areaMap = mapVersion.areaMap;
    const mapFloors = areaMap.mapFloors;
    const mapFeatures = mapFloors.models.reduce(
      (mapFeatures, mapFloor) => [...mapFeatures, ...mapFloor.mapFeatures.models],
      [],
    );

    const mapFloorCollection = new Collection('map-floor', mapFloors.models);
    const mapFeatureCollection = new Collection('map-feature', mapFeatures);

    const { data: includedMapFloors } = this.serializerOrRegistry.serialize(mapFloorCollection, request);
    const { data: includedMapFeatures } = this.serializerOrRegistry.serialize(mapFeatureCollection, request);

    const included = [...includedMapFloors, ...includedMapFeatures];
    return {
      data: {
        live: resourceStates,
        static: included,
      },
    };
  });
}
